<template>
    <div class="sod-eod-report-summary">
        <v-progress-circular v-if="loading" indeterminate color="primary" class="d-block mx-auto my-6" />
        <template v-else-if="report">
            <v-row dense class="mb-3">
                <v-col v-for="tile in statTiles" :key="tile.label" cols="6" sm="4" md="2">
                    <v-card outlined rounded="lg" class="stat-tile pa-3 fill-height d-flex align-center">
                        <div class="stat-icon-badge mr-3" :style="{ background: tile.iconBg, color: tile.iconColor }">
                            <v-icon :color="tile.iconColor">{{ tile.icon }}</v-icon>
                        </div>
                        <div class="flex-grow-1">
                            <div class="stat-value">{{ tile.value }}</div>
                            <div class="stat-label">{{ tile.label }}</div>
                        </div>
                        <SodEodProgressRing v-if="tile.percent !== undefined" :percent="tile.percent" :color="tile.ringColor" />
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" md="4">
                    <v-card outlined rounded="lg" class="report-panel pa-4 mb-3 fill-height">
                        <div class="d-flex align-center mb-3">
                            <v-icon size="18" color="primary" class="mr-2">mdi-chart-bar</v-icon>
                            <span class="panel-title">Planned vs Completed</span>
                            <v-spacer />
                            <div v-if="trendDays.length" class="d-flex align-center">
                                <span class="panel-subtitle mr-1">{{ formatShortDate(currentTrendDay.work_date) }}</span>
                                <v-btn icon x-small :disabled="trendDays.length < 2" @click="stepTrendDay">
                                    <v-icon small>mdi-chevron-right</v-icon>
                                </v-btn>
                            </div>
                        </div>
                        <div v-if="!trendDays.length" class="text-body-2 grey--text">No EOD submissions in this range.</div>
                        <div v-else class="pvc-rows">
                            <div class="pvc-row">
                                <span class="pvc-label">Planned</span>
                                <div class="pvc-bar-track">
                                    <div class="pvc-bar pvc-bar-planned" style="width: 100%;" />
                                </div>
                                <span class="pvc-count">{{ currentTrendDay.planned_count }}</span>
                            </div>
                            <div class="pvc-row">
                                <span class="pvc-label">Completed</span>
                                <div class="pvc-bar-track">
                                    <div class="pvc-bar pvc-bar-completed" :style="{ width: completedPct + '%' }" />
                                </div>
                                <span class="pvc-count">{{ currentTrendDay.completed_count }}</span>
                            </div>
                        </div>
                    </v-card>
                </v-col>

                <v-col cols="12" md="4">
                    <v-card outlined rounded="lg" class="report-panel pa-4 mb-3 fill-height">
                        <div class="d-flex align-center mb-3">
                            <v-icon size="18" color="deep-orange" class="mr-2">mdi-alert-circle-outline</v-icon>
                            <span class="panel-title">What Got In The Way</span>
                            <v-spacer />
                            <span class="panel-subtitle">{{ rangeLabel }}</span>
                        </div>
                        <div v-if="!incompleteReasons.length" class="text-body-2 grey--text">
                            No incomplete-day reasons recorded in this range.
                        </div>
                        <div v-else>
                            <div v-for="reason in incompleteReasons" :key="reason.key" class="reason-row mb-3">
                                <div class="d-flex justify-space-between text-body-2 mb-1">
                                    <span>{{ reason.label }}</span>
                                    <span class="font-weight-medium">{{ reason.count }}</span>
                                </div>
                                <div class="reason-bar-track">
                                    <div class="reason-bar" :style="{ width: `${reason.pct}%` }" />
                                </div>
                            </div>
                        </div>
                    </v-card>
                </v-col>

                <v-col cols="12" md="4">
                    <v-card outlined rounded="lg" class="report-panel pa-4 mb-3 fill-height">
                        <div class="d-flex align-center mb-3">
                            <v-icon size="18" color="primary" class="mr-2">mdi-chart-donut</v-icon>
                            <span class="panel-title">Workload Distribution</span>
                            <v-spacer />
                            <span class="panel-subtitle">{{ rangeLabel }}</span>
                        </div>
                        <div v-if="!workloadTotal" class="text-body-2 grey--text">No EOD submissions in this range.</div>
                        <template v-else>
                            <div class="distribution-bar mb-3">
                                <div v-for="seg in workloadSegments" v-show="seg.count > 0" :key="seg.key"
                                    class="distribution-seg" :class="`pulse-${seg.key}`" :style="{ width: `${seg.pct}%` }" />
                            </div>
                            <div class="d-flex flex-wrap" style="gap: 20px;">
                                <span v-for="seg in workloadSegments" :key="`legend-${seg.key}`"
                                    class="d-inline-flex align-center text-body-2" style="gap:6px;">
                                    <span class="legend-dot" :class="`pulse-${seg.key}`"></span>
                                    {{ seg.label }} <span class="grey--text ml-1">{{ seg.count }}</span>
                                </span>
                            </div>
                        </template>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" md="6">
                    <v-card outlined rounded="lg" class="report-panel pa-4 mb-3 fill-height">
                        <div class="d-flex align-center mb-3">
                            <v-icon size="18" color="primary" class="mr-2">mdi-clock-outline</v-icon>
                            <span class="panel-title">EOD Reporting Consistency</span>
                        </div>
                        <div v-if="!eodConsistencyTotal" class="text-body-2 grey--text">No EOD submissions in this range.</div>
                        <template v-else>
                            <div class="text-body-2 mb-2">
                                Submitted before {{ report.eod_consistency.eod_prompt_hour }}:00 (local):
                                <strong>{{ report.eod_consistency.on_time }}</strong> of {{ eodConsistencyTotal }}
                            </div>
                            <div class="distribution-bar mb-2">
                                <div class="distribution-seg pulse-comfortable"
                                    :style="{ width: `${(report.eod_consistency.on_time / eodConsistencyTotal) * 100}%` }" />
                                <div class="distribution-seg pulse-too_much"
                                    :style="{ width: `${(report.eod_consistency.late / eodConsistencyTotal) * 100}%` }" />
                            </div>
                            <div class="text-caption grey--text">
                                Note: Start of Day has no configured due time, so SOD submissions are shown by time only
                                in the history list below — not judged as early or late.
                            </div>
                        </template>
                    </v-card>
                </v-col>

                <v-col cols="12" md="6">
                    <v-card outlined rounded="lg" class="report-panel pa-4 mb-3 fill-height">
                        <div class="d-flex align-center mb-3">
                            <v-icon size="18" color="primary" class="mr-2">mdi-timer-outline</v-icon>
                            <span class="panel-title">Time Tracked on Planned Tasks</span>
                        </div>
                        <div class="tracked-time-value">{{ formatDuration(report.tracked_seconds_total) }}</div>
                        <div class="text-caption grey--text mt-1">
                            Total time logged against tasks that were part of a Start of Day plan in this range.
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </template>
        <div v-else class="text-body-2 grey--text py-4">No report data available.</div>
    </div>
</template>

<script>
import SodEodProgressRing from './SodEodProgressRing.vue';

export default {
    name: 'SodEodReportSummary',
    components: {
        SodEodProgressRing,
    },
    props: {
        report: {
            type: Object,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            trendIndex: 0,
        };
    },
    computed: {
        statTiles() {
            if (!this.report) return [];
            const r = this.report;
            const pct = (num, den) => (den > 0 ? (num / den) * 100 : 0);
            const completionPct = r.completion_rate !== null ? Math.round(r.completion_rate * 100) : 0;
            return [
                {
                    icon: 'mdi-calendar-month-outline', iconBg: '#eaf2fe', iconColor: '#2f6fed',
                    value: r.business_days, label: 'Business days',
                },
                {
                    icon: 'mdi-white-balance-sunny', iconBg: '#e8f8ee', iconColor: '#22a35e',
                    value: `${r.days_with_sod}/${r.business_days}`, label: 'Days with SOD',
                    percent: pct(r.days_with_sod, r.business_days), ringColor: '#22a35e',
                },
                {
                    icon: 'mdi-weather-night', iconBg: '#f2edfc', iconColor: '#8b5cf6',
                    value: `${r.days_with_eod}/${r.business_days}`, label: 'Days with EOD',
                    percent: pct(r.days_with_eod, r.business_days), ringColor: '#8b5cf6',
                },
                {
                    icon: 'mdi-file-remove-outline', iconBg: '#fdeceb', iconColor: '#e5484d',
                    value: r.missing_sod_days, label: 'Missing SOD',
                },
                {
                    icon: 'mdi-file-remove-outline', iconBg: '#fdeceb', iconColor: '#e5484d',
                    value: r.missing_eod_days, label: 'Missing EOD',
                },
                {
                    icon: 'mdi-check-circle-outline', iconBg: '#eaf2fe', iconColor: '#2f6fed',
                    value: `${r.completed_count}/${r.planned_count}`, label: 'Tasks Completed',
                    percent: completionPct, ringColor: '#2f6fed',
                },
            ];
        },
        rangeLabel() {
            if (!this.report) return '';
            return `${this.formatShortDate(this.report.date_from)} – ${this.formatShortDate(this.report.date_to)}`;
        },
        trendDays() {
            return this.report ? this.report.trend : [];
        },
        currentTrendDay() {
            if (!this.trendDays.length) return null;
            const idx = Math.min(this.trendIndex, this.trendDays.length - 1);
            // Most recent day first.
            return this.trendDays.slice().reverse()[idx];
        },
        completedPct() {
            if (!this.currentTrendDay || !this.currentTrendDay.planned_count) return 0;
            return (this.currentTrendDay.completed_count / this.currentTrendDay.planned_count) * 100;
        },
        workloadSegments() {
            if (!this.report) return [];
            const counts = this.report.workload_counts || {};
            const labels = { comfortable: 'Comfortable', about_right: 'About Right', too_much: 'Too Much' };
            const total = this.workloadTotal || 1;
            return Object.keys(labels).map((key) => ({
                key,
                label: labels[key],
                count: counts[key] || 0,
                pct: ((counts[key] || 0) / total) * 100,
            }));
        },
        workloadTotal() {
            if (!this.report) return 0;
            const counts = this.report.workload_counts || {};
            return Object.values(counts).reduce((a, b) => a + b, 0);
        },
        eodConsistencyTotal() {
            if (!this.report) return 0;
            const c = this.report.eod_consistency || {};
            return (c.on_time || 0) + (c.late || 0);
        },
        incompleteReasons() {
            if (!this.report) return [];
            const labels = {
                higher_priority: 'Higher priority came up',
                waiting_on_someone: 'Waiting on someone',
                larger_than_expected: 'Larger than expected',
                blocked: 'Blocked',
                other: 'Other',
            };
            const counts = this.report.incomplete_reason_counts || {};
            const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
            return Object.keys(counts)
                .map((key) => ({ key, label: labels[key] || key, count: counts[key], pct: (counts[key] / total) * 100 }))
                .sort((a, b) => b.count - a.count);
        },
    },
    watch: {
        report() {
            this.trendIndex = 0;
        },
    },
    methods: {
        stepTrendDay() {
            if (this.trendDays.length < 2) return;
            this.trendIndex = (this.trendIndex + 1) % this.trendDays.length;
        },
        formatShortDate(date) {
            if (!date) return '—';
            return this.$date(date).format('MMM D, YYYY');
        },
        formatDuration(seconds) {
            const s = seconds || 0;
            const hours = Math.floor(s / 3600);
            const minutes = Math.floor((s % 3600) / 60);
            if (hours === 0 && minutes === 0) return '0m';
            return `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;
        },
    },
};
</script>

<style scoped>
.stat-tile {
    border-color: rgba(0, 0, 0, 0.08) !important;
}

.stat-icon-badge {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.stat-value {
    font-size: 1.2rem;
    font-weight: 700;
    line-height: 1.2;
}

.stat-label {
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.55);
    white-space: nowrap;
}

.report-panel {
    border-color: rgba(0, 0, 0, 0.08) !important;
}

.panel-title {
    font-size: 0.95rem;
    font-weight: 700;
}

.panel-subtitle {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.5);
}

.pvc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.pvc-label {
    width: 78px;
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
    flex-shrink: 0;
}

.pvc-bar-track {
    flex: 1;
    height: 10px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.pvc-bar {
    height: 100%;
    border-radius: 6px;
}

.pvc-bar-planned {
    background: linear-gradient(90deg, #bcd4fb, #8fb8f8);
}

.pvc-bar-completed {
    background: linear-gradient(90deg, #4f8ef7, #2f6fed);
}

.pvc-count {
    width: 24px;
    text-align: right;
    font-size: 0.85rem;
    font-weight: 600;
}

.distribution-bar {
    display: flex;
    height: 16px;
    border-radius: 8px;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.05);
}

.distribution-seg {
    height: 100%;
}

.pulse-comfortable {
    background: linear-gradient(90deg, #34d17c, #16a34a);
}

.pulse-about_right {
    background: linear-gradient(90deg, #fbcc5c, #f5b219);
}

.pulse-too_much {
    background: linear-gradient(90deg, #f27a7a, #e5484d);
}

.legend-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.reason-bar-track {
    height: 8px;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.reason-bar {
    height: 100%;
    border-radius: 5px;
    background: linear-gradient(90deg, #a78bfa, #7c5cd6);
}

.tracked-time-value {
    font-size: 1.6rem;
    font-weight: 700;
}
</style>
